import React, { useEffect } from 'react';
import WeeklyCalendarGrid from '../components/WeeklyCalendarGrid';

import { isoToday, startOfWeekISO, addDaysISO } from '../data/mockCalendarData';
import { EmptyCellActionSheet } from '../modal/EmptyCellActionSheet';
import { SessionCreateModal } from '../modal/SessionCreateModal/SessionCreateModal';
import { SessionEditModal } from '../modal/SessionEditModal/SessionEditModal';
import { ConfirmDialog } from '../modal/parts/ConfirmDialog';
import { sessionsApi } from '../api/sessionsApi';
import { useWeeklySessions } from '../hooks/useWeeklySessions';
import { useSessionFlows } from '../hooks/useSessionFlows';
import { WeekCopyWizardModal } from '../components/WeekCopyWizardModal';
import SessionDetailDialog from '../components/calendar/sessionDetail/SessionDetailDialog';
import { useQuickActionsBridge } from 'shared/ui/QuickActions/QuickActionsBridge';
import TodaySessionsModal from '../components/TodaySessionsModal';
import { staffApi } from 'sectors/pilates/staff/api/staffApi';
import { AttendanceQueueModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/modals/AttendanceQueueModal/AttendanceQueueModal';
import { attendanceApi } from 'sectors/pilates/attendance/api/attendanceApi';
import { useCompanySettings } from 'core/companySettings';
import { buildTimeSlots, toolsToCalendarConfig } from '../components/calendar/utils';
import { AttendanceMarkModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/AttendanceMarkModal';
import { formatApiError } from 'shared/errorUtils';

export default function PilatesSessionsPage() {
  const [trainers, setTrainers] = React.useState([{ id: 'ALL', name: 'Tümü' }]);
  const [selectedTrainerId, setSelectedTrainerId] = React.useState('');

  const [anchorISO, setAnchorISO] = React.useState(isoToday());
  const weekStartISO = React.useMemo(() => startOfWeekISO(anchorISO), [anchorISO]);

  const days = React.useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDaysISO(weekStartISO, i)),
    [weekStartISO]
  );

  const { settings, loading: settingsLoading } = useCompanySettings();
  // settings burada /api/v1/company-settings/me çıktısı

  const tools = settings?.sectorSettings?.tools ?? settings?.tools ?? {};
  const cfg = React.useMemo(() => toolsToCalendarConfig(tools || {}), [tools]);

  const calType = String(cfg?.calendarType || 'TAM_SAAT');
  const mustPickTrainer = calType === 'YARIM_SAAT_YARIM';
  useEffect(() => {
    console.log('[calendar] calType =', calType, 'cfg=', cfg);
  }, [calType, cfg]);

  React.useEffect(() => {
    const real = (trainers || []).filter((t) => t && String(t.id) !== 'ALL');
    const firstRealId = real.length ? String(real[0].id) : '';

    if (mustPickTrainer) {
      // yarım-yarım modunda ALL veya boş olamaz → ilk eğitmeni zorla
      if ((!selectedTrainerId || selectedTrainerId === 'ALL') && firstRealId) {
        setSelectedTrainerId(firstRealId);
      }
    } else {
      // diğer modlarda boşsa ALL yap
      if (!selectedTrainerId) {
        setSelectedTrainerId('ALL');
      }
    }
  }, [mustPickTrainer, trainers, selectedTrainerId]);

  // buildTimeSlots => [{minutes,label,hour,minute}]
  const slotObjs = React.useMemo(() => buildTimeSlots(cfg), [cfg]);

  // WeeklyCalendarGrid şu an number[] bekliyor
  const slots = React.useMemo(() => slotObjs.map((x) => x.minutes), [slotObjs]);

  // (opsiyonel) slot doğrulama / snap
  const slotSet = React.useMemo(() => new Set(slots), [slots]);
  const snapToSlot = React.useCallback(
    (min) => {
      const x = Number(min);
      if (!Number.isFinite(x)) return slots[0] ?? 0;
      if (slotSet.has(x)) return x;

      let best = slots[0] ?? 0;
      let bestD = Math.abs(x - best);
      for (const t of slots) {
        const d = Math.abs(x - t);
        if (d < bestD) {
          best = t;
          bestD = d;
        }
      }
      return best;
    },
    [slots, slotSet]
  );
  const { sessions, loading, fetchWeekly } = useWeeklySessions(weekStartISO);

  const sessionsForGrid = React.useMemo(() => {
    const list = Array.isArray(sessions) ? sessions : [];
    if (!list.length) return list;

    return list.map((s) => {
      const raw = Number(s?.startMin);
      if (!Number.isFinite(raw)) return s;

      const snapped = snapToSlot(raw);
      // sadece UI için düzelt; backend’e yazma
      return snapped === raw ? s : { ...s, startMin: snapped, _snapped: true, _rawStartMin: raw };
    });
  }, [sessions, snapToSlot]);

  const flows = useSessionFlows({ fetchWeekly });

  function hm(min) {
    const m = Number(min) || 0;
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  const formatWarnings = (warnings) => {
    const ws = Array.isArray(warnings) ? warnings : [];
    if (!ws.length) return null;

    // backend: {code,message} bekliyoruz ama bazı yerlerde string olabilir
    const lines = ws
      .map((w) => {
        if (!w) return null;
        if (typeof w === 'string') return `• ${w}`;
        return `• ${w.message || w.msg || JSON.stringify(w)}`;
      })
      .filter(Boolean);

    if (!lines.length) return null;

    return ['', 'Uyarılar:', ...lines].join('\n');
  };

  function pickActivityName(s) {
    return s?.activityName || s?.activityNameSnapshot || s?.activity?.name || s?.title || 'Seans';
  }

  function pickTrainerName(s) {
    return s?.trainerNameSnapshot || s?.trainerName || s?.trainer?.name || '—';
  }

  function pickMembers(s) {
    // participants varsa: snapshot adlar
    if (Array.isArray(s?.participants)) {
      const names = s.participants
        .map((p) => p?.fullName || p?.memberNameSnapshot || p?.memberName)
        .filter(Boolean);
      return names;
    }
    // members array varsa:
    if (Array.isArray(s?.members)) {
      return s.members.map((x) => String(x)).filter(Boolean);
    }
    return [];
  }

  function buildDeleteConfirmMessage(s) {
    if (!s) return 'Emin misin?';

    const day = s?.dayISO || s?.day || s?.date || '';
    const time = hm(s?.startMin);
    const activity = pickActivityName(s);
    const trainer = pickTrainerName(s);

    const cap = Number.isFinite(s?.capacity) ? s.capacity : Number.isFinite(s?.cap) ? s.cap : null;
    const members = pickMembers(s);
    const booked = Number.isFinite(s?.participantCount) ? s.participantCount : members.length;

    const membersText = members.length
      ? members.slice(0, 6).join(', ') + (members.length > 6 ? ` +${members.length - 6}` : '')
      : '—';

    // Çok okunaklı, multi-line
    return [
      'Bu seansı silmek istediğine emin misin?',
      '',
      `Tarih / Saat: ${day} • ${time}`,
      `Seans: ${activity}`,
      `Eğitmen: ${trainer}`,
      cap != null ? `Kontenjan: ${booked}/${cap}` : `Katılımcı: ${booked}`,
      `Üyeler: ${membersText}`,
    ].join('\n');
  }
  const [copyMode, setCopyMode] = React.useState(false); // ✅ takvimden seans seçme modu

  // ✅ copy flow
  const [copyConfirmOpen, setCopyConfirmOpen] = React.useState(false);
  const [copyBusy, setCopyBusy] = React.useState(false);

  const [copyTarget, setCopyTarget] = React.useState(null); // {dayISO,startMin}
  const [copySource, setCopySource] = React.useState(null); // session object (kopyalanacak)
  const [copyBlock, setCopyBlock] = React.useState(null); // {message, members?}
  const [copyWarn, setCopyWarn] = React.useState(null); // {message, members?} veya string
  const buildCopyPayload = React.useCallback(() => {
    if (!copySource?.id || !copyTarget?.dayISO) return null;
    return {
      sourceSessionId: Number(copySource.id),
      to: { dayISO: copyTarget.dayISO, startMin: Number(copyTarget.startMin) || 0 },
    };
  }, [copySource, copyTarget]);

  const decisionToBlock = (d) => d?.blocks?.[0] || null;
  const decisionToWarnMsg = (d) => {
    const ws = Array.isArray(d?.warnings) ? d.warnings : [];
    if (!ws.length) return null;
    return ws.length === 1 ? ws[0].message : ws.map((x) => `• ${x.message}`).join('\n');
  };
  // ✅ MOVE FLOW (desktop + mobile ortak)
  const [moveConfirmOpen, setMoveConfirmOpen] = React.useState(false);
  const [moveBusy, setMoveBusy] = React.useState(false);
  const [movePayload, setMovePayload] = React.useState(null); // { sessionId, to:{dayISO,startMin} }
  const [moveWarn, setMoveWarn] = React.useState(null); // { message }
  const [moveBlock, setMoveBlock] = React.useState(null); // { message }
  function normalizeMovePayload(p) {
    if (!p) return null;

    const sessionId =
      p.sessionId ?? p.id ?? p.sourceSessionId ?? p.fromSessionId ?? p.session?.id ?? null;

    const dayISO =
      p?.to?.dayISO ?? p?.dayISO ?? p?.toDayISO ?? p?.target?.dayISO ?? p?.to?.date ?? null;

    const startMin = p?.to?.startMin ?? p?.startMin ?? p?.toStartMin ?? p?.target?.startMin ?? null;

    if (!sessionId || !dayISO) return null;

    return {
      sessionId: Number(sessionId),
      to: { dayISO: String(dayISO).slice(0, 10), startMin: Number(startMin) || 0 },
    };
  }

  const doMove = React.useCallback(
    async ({ force = false } = {}) => {
      if (!movePayload?.sessionId || !movePayload?.to?.dayISO) return;

      try {
        setMoveBusy(true);

        // 1) validate
        const decision = await sessionsApi.moveValidate(movePayload); // ✅ endpoint adın farklıysa değiştir

        const b = decisionToBlock(decision);
        if (b) {
          setMoveConfirmOpen(false);
          setMoveWarn(null);
          setMoveBlock({ message: b.message || 'Taşıma engellendi.' });
          return;
        }

        const warnMsg = decisionToWarnMsg(decision);
        if (warnMsg && !force) {
          setMoveConfirmOpen(false);
          setMoveWarn({ message: warnMsg });
          return;
        }

        // 2) commit
        await sessionsApi.moveCommit(movePayload); // ✅ endpoint adın farklıysa değiştir

        // 3) reset + refresh
        setMoveConfirmOpen(false);
        setMoveWarn(null);
        setMoveBlock(null);
        setMovePayload(null);

        await fetchWeekly();
      } catch (e) {
        setMoveBlock({ message: formatApiError(e, 'Tasima sirasinda hata olustu.') });
      } finally {
        setMoveBusy(false);
      }
    },
    [movePayload, fetchWeekly]
  );
  const doCopy = React.useCallback(
    async ({ force = false } = {}) => {
      const payload = buildCopyPayload();
      if (!payload) return;

      try {
        setCopyBusy(true);

        // 1) validate
        const decision = await sessionsApi.copyValidate(payload);

        const b = decisionToBlock(decision);
        if (b) {
          setCopyConfirmOpen(false);
          setCopyWarn(null);
          setCopyBlock({ message: b.message || 'Kopyalama engellendi.' });
          return;
        }

        const warnMsg = decisionToWarnMsg(decision);
        if (warnMsg && !force) {
          setCopyConfirmOpen(false);
          setCopyWarn({ message: warnMsg });
          return;
        }

        // 2) commit
        await sessionsApi.copyCommit(payload);

        // 3) reset
        setCopyConfirmOpen(false);
        setCopyWarn(null);
        setCopySource(null);
        setCopyTarget(null);

        await fetchWeekly();
      } catch (e) {
        setCopyBlock({ message: formatApiError(e, 'Kopyalama sirasinda hata olustu.') });
      } finally {
        setCopyBusy(false);
      }
    },
    [buildCopyPayload, fetchWeekly]
  );

  const [weekCopyOpen, setWeekCopyOpen] = React.useState(false);
  const [weekCopyPreview, setWeekCopyPreview] = React.useState(null); // { payload, result, ghostSessions, summary }
  const [weekCopyBusy, setWeekCopyBusy] = React.useState(false);
  const [weekCopyConfirmOpen, setWeekCopyConfirmOpen] = React.useState(false);
  const [weekCopyError, setWeekCopyError] = React.useState(null);

  const trainerIdOrNull =
    selectedTrainerId !== 'ALL' && selectedTrainerId != null && selectedTrainerId !== ''
      ? Number(selectedTrainerId)
      : null;

  const onMobileCopyRequest = React.useCallback(
    (payload) => {
      const s = (sessions || []).find((x) => String(x.id) === String(payload?.sessionId));
      if (!s) return;

      setCopyTarget({
        dayISO: payload.to.dayISO,
        startMin: Number(payload.to.startMin) || 0,
      });
      setCopySource(s);

      setCopyMode(false); // mobilde “takvimden seans seç” moduna gerek yok
      setCopyWarn(null);
      setCopyBlock(null);
      setCopyConfirmOpen(true); // direkt onay ekranını aç
    },
    [sessions]
  );

  const [editMode, setEditMode] = React.useState(false);
  const [attendanceMode, setAttendanceMode] = React.useState(false);
  const [attendanceListOpen, setAttendanceListOpen] = React.useState(false);
  const [queueOpen, setQueueOpen] = React.useState(false);
  const [markOpen, setMarkOpen] = React.useState(false);
  const [markSession, setMarkSession] = React.useState(null);

  // toggle + set destekli
  const onEnterEdit = React.useCallback((next) => {
    setEditMode((prev) => {
      const v = typeof next === 'boolean' ? next : !prev;
      if (v) {
        setAttendanceMode(false);
        setAttendanceListOpen(false);
      }
      return v;
    });
  }, []);

  const onEnterAttendance = React.useCallback((next) => {
    setAttendanceMode((prev) => {
      const v = typeof next === 'boolean' ? next : !prev;
      if (v) setEditMode(false);
      if (!v) setAttendanceListOpen(false);
      return v;
    });
  }, []);

  const openAttendanceList = React.useCallback(() => {
    setAttendanceMode(true);
    setEditMode(false);

    // eski state'i istersen tut, ama UI'da karşılığı yoksa kaldırabilirsin
    setAttendanceListOpen(true);

    // ✅ asıl iş: queue modal aç
    setQueueOpen(true);
  }, []);

  const { register } = useQuickActionsBridge();

  React.useEffect(() => {
    return register('sessions', {
      state: {
        editMode,
        attendanceMode,
      },
      setEditMode: (v) => onEnterEdit(v), // ✅ senin guard’lı fonksiyon
      setAttendanceMode: (v) => onEnterAttendance(v), // ✅ senin guard’lı fonksiyon
      openAttendanceList: () => openAttendanceList(), // ✅ mode + modal açar
      openToday: () => setAnchorISO(isoToday()), // ✅ bugün
    });
  }, [register, editMode, attendanceMode, onEnterEdit, onEnterAttendance, openAttendanceList]);

  const [todayOpen, setTodayOpen] = React.useState(false);
  const todayISO = isoToday();

  const openToday = React.useCallback(() => {
    setAnchorISO(isoToday()); // takvimi bugüne al
    setTodayOpen(true); // modal aç
  }, []);
  React.useEffect(() => {
    return register('sessions', {
      state: { editMode, attendanceMode },
      setEditMode: onEnterEdit,
      setAttendanceMode: onEnterAttendance,
      openAttendanceList: openAttendanceList,

      openToday, // ✅ QuickActions'taki "Bugün" buna basacak
    });
  }, [
    register,
    editMode,
    attendanceMode,
    onEnterEdit,
    onEnterAttendance,
    openAttendanceList,
    openToday,
  ]);
  useEffect(() => {
    let alive = true;

    staffApi
      .trainerPicker()
      .then((items) => {
        if (!alive) return;

        const mapped = (Array.isArray(items) ? items : [])
          .filter((x) => x && x.id != null)
          .map((x) => ({
            id: String(x.id), // ✅ grid filtresi string kullanıyor
            name: x.fullName || '—', // ✅ UI için
          }));

        setTrainers([{ id: 'ALL', name: 'Tümü' }, ...mapped]);
      })
      .catch((e) => {
        console.error('[trainerPicker] failed', e);
        // fallback: ALL kalsın
        setTrainers([{ id: 'ALL', name: 'Tümü' }]);
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <div style={{ padding: 16 }}>
      <WeeklyCalendarGrid
        attendanceListOpen={attendanceListOpen}
        setAttendanceListOpen={setAttendanceListOpen}
        editMode={editMode}
        onEnterEdit={onEnterEdit}
        attendanceMode={attendanceMode}
        onEnterAttendance={onEnterAttendance}
        onOpenAttendanceList={openAttendanceList}
        days={days}
        onMobileCopyRequest={onMobileCopyRequest}
        slots={slots}
        trainers={trainers}
        sessions={sessionsForGrid}
        loading={loading}
        onPrevWeek={() => setAnchorISO((prev) => addDaysISO(prev, -7))}
        onNextWeek={() => setAnchorISO((prev) => addDaysISO(prev, 7))}
        onAddSession={flows.addDefault}
        // ✅ normal mod: tık = detay
        onSessionClick={(s) => {
          if (attendanceMode) {
            // ✅ yoklama modu: direkt yoklama modalını aç
            setMarkSession({
              id: s?.id,
              dayISO: s?.dayISO,
              startMin: s?.startMin,
              trainerId: s?.trainerId,
              trainerName: s?.trainerNameSnapshot || s?.trainerName,
            });
            setMarkOpen(true);
            return;
          }

          // ✅ normal mod: detay aç
          flows.openDetail(s);
        }}
        // ✅ edit mod icon: düzenle/sil

        onSessionEdit={flows.openEdit}
        onSessionDelete={flows.requestDelete}
        onEmptyCellClick={flows.handleEmptyCellClick}
        onMoveRequest={(payload) => {
          const norm = normalizeMovePayload(payload);
          console.log('[move] payload=', payload, 'norm=', norm);

          if (!norm) return; // burada istersen toast/log bas
          setMovePayload(norm);
          setMoveBlock(null);
          setMoveWarn(null);
          setMoveConfirmOpen(true);
        }}
        onMoveSession={(payload) => {
          const norm = normalizeMovePayload(payload);
          console.log('[move desktop] payload=', payload, 'norm=', norm);

          if (!norm) return;
          setMovePayload(norm);
          setMoveBlock(null);
          setMoveWarn(null);
          setMoveConfirmOpen(true);
        }}
        filterTrainerId={selectedTrainerId}
        setFilterTrainerId={setSelectedTrainerId}
        copyMode={copyMode}
        copyTarget={copyTarget}
        onCopyPickSession={(s) => {
          // copyMode açıkken takvimden seans seçildi
          setCopySource(s);
          setCopyMode(false);
          setCopyConfirmOpen(true);
        }}
        onCopyCancel={() => {
          setCopyMode(false);
          setCopyTarget(null);
          setCopySource(null);
          setCopyConfirmOpen(false);
          setCopyWarn(null);
          setCopyBlock(null);
        }}
        onCopyPickTarget={(slot) => {
          if (!slot?.dayISO) return;

          setCopyTarget({ dayISO: slot.dayISO, startMin: Number(slot.startMin) || 0 });
          setCopyMode(true);

          setCopySource(null);
          setCopyConfirmOpen(false);
          setCopyWarn(null);
          setCopyBlock(null);
        }}
        onOpenWeekCopy={() => {
          // burada WeekCopyModal açacağız (bir sonraki adım)
          setWeekCopyOpen(true);
        }}
        ghostSessions={weekCopyPreview?.ghostSessions || []}
        ghostMode={!!weekCopyPreview}
        onToday={() => setAnchorISO(isoToday())}
        onReloadSessions={fetchWeekly} // ✅ EKLE: queue kaydı sonrası liste yenilenecek

        // ✅ BUNU EKLE
      />
      <AttendanceQueueModal
        open={queueOpen}
        onClose={() => setQueueOpen(false)}
        from={days?.[0]}
        to={days?.[6]}
        trainerId={trainerIdOrNull}
        q={null}
        onPick={(it) => {
          setQueueOpen(false);
          setMarkSession({
            id: it?.sessionId,
            dayISO: it?.dayISO,
            startMin: it?.startMin,
            trainerId: it?.trainerId,
            trainerName: it?.trainerNameSnapshot || it?.trainerName,
          });
          setMarkOpen(true);
        }}
        fetchParticipants={async (sid) => {
          const res = await attendanceApi.getSessionParticipants(sid, 5);
          return res?.participants ?? [];
        }}
        onWeeklyRefresh={fetchWeekly}
      />
      <AttendanceMarkModal
        open={markOpen}
        onClose={() => {
          setMarkOpen(false);
          setMarkSession(null);
        }}
        session={markSession}
        trainersById={new Map((trainers || []).map((t) => [String(t.id), t]))}
        fetchParticipants={async (sid) => {
          const res = await attendanceApi.getSessionParticipants(sid, 5);
          return res?.participants ?? [];
        }}
        onSave={async (payload) => attendanceApi.markAttendance(markSession?.id, payload)}
        onWeeklyRefresh={fetchWeekly}
      />

      <WeekCopyWizardModal
        open={weekCopyOpen}
        onClose={() => {
          if (weekCopyBusy) return;
          setWeekCopyOpen(false);
        }}
        defaultFromWeekStartISO={addDaysISO(weekStartISO, -7)} // ✅ 1 hafta önce
        defaultToWeekStartISO={weekStartISO}
        trainerId={trainerIdOrNull}
        onPreview={async (payload) => {
          try {
            setWeekCopyBusy(true);
            setWeekCopyError(null);
            const req = { ...payload, trainerId: trainerIdOrNull };

            const result = await sessionsApi.weekCopyPreview({
              ...payload,
              trainerId: trainerIdOrNull,
            });
            const items = Array.isArray(result?.items) ? result.items : [];

            const ghostSessions = items
              .filter((it) => it?.decision === 'WILL_COPY' && it?.ghost && it?.target?.dayISO)
              .map((it) => {
                const g = it.ghost;

                return {
                  // ✅ takvimde slot’a oturması için şart
                  dayISO: it.target.dayISO,
                  startMin: Number(it.target.startMin) || 0,

                  // ✅ DayCell/SoftSessionCard normalizasyonu bunları da sever
                  trainerId: g.trainerId,
                  trainerNameSnapshot: g.trainerNameSnapshot,

                  activityId: g.activityId,
                  activityNameSnapshot: g.activityNameSnapshot,

                  durationMin: g.durationMin,
                  capacity: g.capacity,

                  billingMode: g.billingMode,
                  sessionType: g.sessionType,

                  // SoftSessionCard participants[] veya members[] bekliyor
                  participants: (g.participantsPreview || []).map((name) => ({
                    memberNameSnapshot: name,
                  })),
                };
              });

            setWeekCopyOpen(false);

            // ✅ hedef haftaya git
            setAnchorISO(payload.toWeekStartISO);

            setWeekCopyPreview({
              payload,
              result,
              ghostSessions,
              summary: result?.summary || null,
              warnings: result?.warnings || [],
              blocks: result?.blocks || [],
            });

            setWeekCopyConfirmOpen(true);
          } catch (e) {
            setWeekCopyError(formatApiError(e, 'Onizleme basarisiz.'));
          } finally {
            setWeekCopyBusy(false);
          }
        }}
      />
      <ConfirmDialog
        open={weekCopyConfirmOpen}
        tone="info"
        title="Haftayı kopyala"
        subtitle="Önizleme hazır"
        busy={weekCopyBusy}
        message={(() => {
          const s = weekCopyPreview?.summary;
          const willCopy = s?.willCopyCount ?? 0;
          const skippedTelafi = s?.skippedTelafiCount ?? 0;
          const skippedFiltered = s?.skippedFilteredCount ?? 0;

          const warnText = formatWarnings(weekCopyPreview?.warnings);

          return [
            'Hedef haftaya önizleme yerleştirildi.',
            '',
            `Kaynak seans: ${s?.sourceCount ?? 0}`,
            `Uygun: ${s?.eligibleCount ?? 0}`,
            `Kopyalanacak: ${willCopy}`,
            `Telafi atlanan: ${skippedTelafi}`,
            `Filtre atlanan: ${skippedFiltered}`,
            warnText || '',
            '',
            'Kaydetmek istiyor musun?',
          ]
            .filter(Boolean)
            .join('\n');
        })()}
        cancelText="Vazgeç"
        confirmText={weekCopyBusy ? 'Kopyalanıyor...' : 'Onayla ve Kopyala'}
        onCancel={() => {
          if (weekCopyBusy) return;
          setWeekCopyConfirmOpen(false);
          setWeekCopyPreview(null);
        }}
        onConfirm={async () => {
          if (!weekCopyPreview?.payload) return;
          try {
            setWeekCopyBusy(true);
            await sessionsApi.weekCopyCommit({
              ...weekCopyPreview.payload,
              trainerId: trainerIdOrNull,
            });

            setWeekCopyConfirmOpen(false);
            setWeekCopyPreview(null);
            await fetchWeekly();
          } catch (e) {
            setWeekCopyError(formatApiError(e, 'Kopyalama basarisiz.'));
          } finally {
            setWeekCopyBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={!!weekCopyError}
        tone="error"
        title="Hafta kopyalama"
        subtitle="Hata"
        message={weekCopyError || 'Bir hata oluştu.'}
        confirmText="Tamam"
        hideCancel
        onConfirm={() => setWeekCopyError(null)}
        onCancel={() => setWeekCopyError(null)}
      />

      <EmptyCellActionSheet
        open={flows.sheetOpen}
        onClose={() => flows.setSheetOpen(false)}
        title={
          flows.emptySlot
            ? `${flows.emptySlot.dayISO} • ${String(flows.emptySlot.startMin)}`
            : 'Boş slot'
        }
        slot={flows.emptySlot} // ✅ EKLE (KRİTİK)
        onAdd={() => flows.setCreateOpen(true)}
        onCopy={(slot) => {
          if (!slot?.dayISO) return;

          // hedef slotu belirle
          setCopyTarget({ dayISO: slot.dayISO, startMin: Number(slot.startMin) || 0 });

          // ✅ takvimden seans seçme moduna gir
          setCopyMode(true);

          // temizle
          setCopySource(null);
          setCopyConfirmOpen(false);
          setCopyWarn(null);
          setCopyBlock(null);
        }}
      />

      <SessionCreateModal
        open={flows.createOpen}
        onClose={() => flows.setCreateOpen(false)}
        dayISO={flows.emptySlot?.dayISO}
        startMin={flows.emptySlot?.startMin}
        defaultTrainerId={selectedTrainerId} // ✅ takvim filtresi neyse o
        onCreated={flows.onCreated}
      />

      <SessionDetailDialog
        open={flows.detailOpen}
        onClose={() => flows.setDetailOpen(false)}
        sessionId={flows.selectedSessionId} // ✅ id ile detay çekilecek
        sessionSnapshot={flows.selectedSession} // ✅ ilk render için (opsiyonel)
        onEdit={flows.openEdit}
      />

      <SessionEditModal
        open={flows.editOpen}
        onClose={() => flows.setEditOpen(false)}
        session={flows.selectedSession}
        onUpdated={flows.onUpdated}
      />

      <ConfirmDialog
        open={flows.deleteConfirmOpen}
        tone={flows.deleteError ? 'error' : 'warning'}
        title={flows.deleteError ? 'Seans silinemedi' : 'Seansı sil'}
        subtitle={
          flows.deleteError
            ? `İşlem reddedildi (${flows.deleteError.status || 0} / ${
                flows.deleteError.code || 'ERROR'
              })`
            : 'Bu işlem geri alınamaz.'
        }
        message={flows.deleteError ? null : buildDeleteConfirmMessage(flows.deletingSession)}
        items={
          flows.deleteError
            ? [
                { title: 'Neden?', message: flows.deleteError.message },
                flows.deleteError.requestId
                  ? { title: 'RequestId', message: flows.deleteError.requestId }
                  : null,
              ].filter(Boolean)
            : null
        }
        confirmText={flows.deleting ? 'İşleniyor...' : flows.deleteError ? 'Tekrar Dene' : 'Sil'}
        cancelText="Vazgeç"
        onCancel={flows.cancelDelete}
        onConfirm={flows.confirmDelete}
        busy={flows.deleting}
      />

      <ConfirmDialog
        open={!!copyBlock}
        tone="error"
        title="Çakışma"
        subtitle="Bu işlem engellendi"
        message={copyBlock?.message || 'Kopyalama engellendi.'}
        confirmText="Tamam"
        hideCancel
        onCancel={() => setCopyBlock(null)}
        onConfirm={() => setCopyBlock(null)}
      />
      <ConfirmDialog
        open={!!copyWarn}
        tone="warning"
        title="Emin misin?"
        subtitle="Uyarı"
        busy={copyBusy}
        message={copyWarn?.message || copyWarn || 'Bu işlem için onay gerekiyor.'}
        cancelText="Vazgeç"
        confirmText={copyBusy ? 'Kopyalanıyor...' : 'Evet, kopyala'}
        onCancel={() => {
          if (copyBusy) return;
          setCopyWarn(null);
          setCopySource(null);
          setCopyTarget(null);
        }}
        onConfirm={async () => {
          await doCopy({ force: true }); // ✅ uyarıya rağmen devam
        }}
      />
      <ConfirmDialog
        open={copyConfirmOpen}
        tone="info"
        title="Seansı kopyala"
        subtitle="Lütfen onayla"
        busy={copyBusy}
        message={
          copySource
            ? [
                'Bu seansı buraya kopyalamak istiyor musun?',
                '',
                `Seans: ${
                  copySource.activityName ||
                  copySource.activityNameSnapshot ||
                  copySource.title ||
                  'Seans'
                }`,
                `Eğitmen: ${copySource.trainerNameSnapshot || copySource.trainerName || '—'}`,
                '',
                `Nereye: ${copyTarget?.dayISO || ''} • ${hm(copyTarget?.startMin || 0)}`,
              ].join('\n')
            : 'Bu seansı buraya kopyalamak istiyor musun?'
        }
        cancelText="Vazgeç"
        confirmText={copyBusy ? 'Kopyalanıyor...' : 'Evet, kopyala'}
        onCancel={() => {
          if (copyBusy) return;
          setCopyConfirmOpen(false);
          setCopySource(null);
          setCopyTarget(null);
        }}
        onConfirm={async () => {
          await doCopy({ force: false }); // ✅ önce validate, warning çıkarsa ayrı ekrana
        }}
      />
      <TodaySessionsModal
        open={todayOpen}
        onClose={() => setTodayOpen(false)}
        todayISO={todayISO}
        sessions={sessions}
        trainers={trainers} // şimdilik ALL var, sonra backend’den trainer listesi
      />
      <ConfirmDialog
        open={!!moveBlock}
        tone="error"
        title="Çakışma"
        subtitle="Bu işlem engellendi"
        message={moveBlock?.message || 'Taşıma engellendi.'}
        confirmText="Tamam"
        hideCancel
        onConfirm={() => setMoveBlock(null)}
        onCancel={() => setMoveBlock(null)}
      />

      <ConfirmDialog
        open={!!moveWarn}
        tone="warning"
        title="Emin misin?"
        subtitle="Uyarı"
        busy={moveBusy}
        message={moveWarn?.message || 'Bu taşıma için onay gerekiyor.'}
        cancelText="Vazgeç"
        confirmText={moveBusy ? 'Taşınıyor...' : 'Evet, taşı'}
        onCancel={() => {
          if (moveBusy) return;
          setMoveWarn(null);
          setMovePayload(null);
        }}
        onConfirm={async () => {
          await doMove({ force: true });
        }}
      />

      <ConfirmDialog
        open={moveConfirmOpen}
        tone="info"
        title="Seansı taşı"
        subtitle="Lütfen onayla"
        busy={moveBusy}
        message={
          movePayload
            ? [
                'Bu seansı seçilen slot’a taşımak istiyor musun?',
                '',
                `Nereye: ${movePayload.to.dayISO} • ${hm(movePayload.to.startMin || 0)}`,
              ].join('\n')
            : 'Bu seansı taşımak istiyor musun?'
        }
        cancelText="Vazgeç"
        confirmText={moveBusy ? 'Taşınıyor...' : 'Evet, taşı'}
        onCancel={() => {
          if (moveBusy) return;
          setMoveConfirmOpen(false);
          setMovePayload(null);
        }}
        onConfirm={async () => {
          await doMove({ force: false });
        }}
      />
    </div>
  );
}
